import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { DollarSign, RefreshCw, ArrowDownRight, Ban, Wallet, BarChart3, Receipt, Lock, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { TakePaymentDialog } from '../../components/TakePaymentDialog';
import { PaymentModal } from '../../components/PaymentModal';
import { PendingEtransfersWidget } from '../../components/PendingEtransfersWidget';
import { DailyRegisterPanel } from '../../components/DailyRegisterPanel';
import { DateRangeBar, DEFAULT_PAYMENTS_PRESETS, todayISO } from '../../components/DateRangeBar';
import { BookingStatusBadge } from '../../components/BookingStatusBadge';
import useMessageStream from '../../hooks/useMessageStream';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CrmPayments = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [refundInsights, setRefundInsights] = useState(null);
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundOpen, setRefundOpen] = useState(false);
  const [posOpen, setPosOpen] = useState(false);
  const [refundBookingId, setRefundBookingId] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [activeTab, setActiveTab] = useState('today');
  const [outstanding, setOutstanding] = useState([]);
  const [outstandingSearch, setOutstandingSearch] = useState('');
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [rangeTotals, setRangeTotals] = useState(null);
  const [rangeTransactions, setRangeTransactions] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [ledgerFilter, setLedgerFilter] = useState({ method: '', type: '', date_from: '', date_to: '' });

  // iter-140 — page-level date range used by all tabs except Ledger
  const [rangePreset, setRangePreset] = useState('today');
  const [range, setRange] = useState({ start: todayISO(), end: todayISO() });
  const [rangeLabel, setRangeLabel] = useState('Today');
  const [rangeLoading, setRangeLoading] = useState(false);

  const rangeParams = useMemo(() => {
    const p = {};
    if (range.start) p.date_from = range.start;
    if (range.end) p.date_to = range.end;
    return p;
  }, [range.start, range.end]);

  // ── Per-tab fetchers (all respect rangeParams) ─────────────────────
  const fetchRangeTotals = useCallback(async () => {
    setRangeLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/range-totals`, {
        params: rangeParams,
        withCredentials: true,
      });
      setRangeTotals(res.data);
    } catch (e) { toast.error('Failed to load totals'); }
    finally { setRangeLoading(false); }
  }, [rangeParams]);

  const fetchRangeTransactions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/unified`, {
        params: { ...rangeParams, limit: 500 },
        withCredentials: true,
      });
      setRangeTransactions(res.data.items || []);
    } catch (e) { /* silent — totals already toasted */ }
  }, [rangeParams]);

  const fetchOutstanding = useCallback(async (search = '') => {
    try {
      const params = { ...rangeParams };
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/api/admin/outstanding-balances`, {
        params,
        withCredentials: true,
      });
      setOutstanding(res.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load outstanding'); }
  }, [rangeParams]);

  const fetchStripeRaw = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments`, {
        params: rangeParams,
        withCredentials: true,
      });
      setPayments(res.data || []);
    } catch (e) { toast.error('Failed to load Stripe payments'); }
  }, [rangeParams]);

  const fetchRefunds = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/refunds`, {
        params: rangeParams,
        withCredentials: true,
      });
      setRefunds(res.data || []);
    } catch (e) { toast.error('Failed to load refunds'); }
  }, [rangeParams]);

  const fetchRefundInsights = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/refund-insights`, {
        params: rangeParams,
        withCredentials: true,
      });
      setRefundInsights(res.data);
    } catch (e) { /* non-blocking summary */ }
  }, [rangeParams]);

  const fetchCancellations = useCallback(async () => {
    try {
      const params = {};
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      const res = await axios.get(`${API_URL}/api/admin/cancellation-log`, {
        params,
        withCredentials: true,
      });
      setCancellations(res.data || []);
    } catch (e) { setCancellations([]); }
  }, [range.start, range.end]);

  const fetchLedger = useCallback(async () => {
    try {
      const params = {};
      Object.entries(ledgerFilter).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API_URL}/api/admin/payments/unified`, {
        params, withCredentials: true,
      });
      setLedger(res.data.items || []);
    } catch (e) { toast.error('Failed to load ledger'); }
  }, [ledgerFilter]);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchRangeTotals(), fetchRangeTransactions(), fetchStripeRaw(), fetchRefunds(), fetchCancellations()]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Tab-switch loads
  useEffect(() => {
    if (activeTab === 'outstanding') fetchOutstanding(outstandingSearch);
    if (activeTab === 'ledger') fetchLedger();
    if (activeTab === 'today') { fetchRangeTotals(); fetchRangeTransactions(); }
    if (activeTab === 'payments') fetchStripeRaw();
    if (activeTab === 'refunds') { fetchRefunds(); fetchRefundInsights(); }
    if (activeTab === 'cancellations') fetchCancellations();
  }, [activeTab]);

  // Range-change re-fetch (all tabs except ledger which has its own filter)
  useEffect(() => {
    if (activeTab === 'ledger' || activeTab === 'register') return;
    if (activeTab === 'today') { fetchRangeTotals(); fetchRangeTransactions(); }
    if (activeTab === 'outstanding') fetchOutstanding(outstandingSearch);
    if (activeTab === 'payments') fetchStripeRaw();
    if (activeTab === 'refunds') { fetchRefunds(); fetchRefundInsights(); }
    if (activeTab === 'cancellations') fetchCancellations();
  }, [range.start, range.end]);

  const refreshAllRangeTabs = () => {
    fetchRangeTotals(); fetchRangeTransactions();
    fetchStripeRaw(); fetchRefunds(); fetchCancellations();
  };

  // Real-time: a booking's status/payment changing (payment, cron hold-expiry,
  // admin override) affects outstanding balances + range totals, so refresh the
  // currently-visible tab when the CRM broadcasts a booking change.
  useMessageStream((evt) => {
    if (!evt) return;
    const relevant = evt.type === 'booking_updated'
      || evt.type === 'calendar_changed'
      || (evt.type === 'crm_changed' && evt.section === 'bookings');
    if (!relevant) return;
    if (activeTab === 'outstanding') fetchOutstanding(outstandingSearch);
    else if (activeTab === 'today') { fetchRangeTotals(); fetchRangeTransactions(); }
    else if (activeTab === 'cancellations') fetchCancellations();
    else if (activeTab === 'refunds') { fetchRefunds(); fetchRefundInsights(); }
    else if (activeTab === 'payments') fetchStripeRaw();
    else if (activeTab === 'ledger') fetchLedger();
  }, { enabled: true });

  const processRefund = async () => {
    if (!refundBookingId || !refundAmount) { toast.error('Fill all fields'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/refunds`, {
        booking_id: refundBookingId, amount: parseFloat(refundAmount), reason: refundReason,
      }, { withCredentials: true });
      toast.success('Refund processed');
      setRefundOpen(false);
      setRefundBookingId(''); setRefundAmount(''); setRefundReason('');
      refreshAllRangeTabs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // iter-142b — First summary card also swaps based on active tab so each
  // tab tells a fully bespoke 3-card story. Third card (Net Revenue)
  // remains constant on every tab as the high-level KPI.
  const firstCard = useMemo(() => {
    if (activeTab === 'outstanding') {
      // Days Past Due (Avg) across rows where party date is in the past
      // AND there's still an outstanding balance. Skips future parties
      // (not yet due) so the average reflects ACTUAL overdue exposure.
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const overdue = outstanding.filter((b) => {
        if (!b.date || Number(b.outstanding || 0) <= 0) return false;
        return new Date(b.date) < today;
      });
      const totalDays = overdue.reduce((s, b) => {
        const d = new Date(b.date);
        const days = Math.max(0, Math.floor((today - d) / 86400000));
        return s + days;
      }, 0);
      const avg = overdue.length > 0 ? totalDays / overdue.length : 0;
      return {
        label: 'Days past due (avg)',
        amount: avg,
        format: (v) => `${v.toFixed(1)}d`,
        color: '#EF4444',           // red — overdue exposure
        testId: 'range-days-past-due',
        title: `Avg days past due across ${overdue.length} overdue booking${overdue.length === 1 ? '' : 's'} · ${rangeLabel}`,
      };
    }
    if (activeTab === 'cancellations') {
      // Net Cancellation Loss = sum(total_paid) − sum(refund_amount).
      // Money the business retained from cancelled bookings (because
      // refund policies don't always pay back 100%).
      const totalCollected = cancellations.reduce((s, c) => s + Number(c.total_paid || 0), 0);
      const totalRefunded = cancellations.reduce((s, c) => s + Number(c.refund_amount || 0), 0);
      const netLoss = totalCollected - totalRefunded;
      return {
        label: 'Net cancellation loss',
        amount: netLoss,
        format: (v) => `$${v.toFixed(2)}`,
        color: '#F59E0B',           // amber — kept revenue (not great, not bad)
        testId: 'range-net-cancellation-loss',
        title: `Collected − refunded for cancellations · ${rangeLabel}`,
      };
    }
    // 'today' / 'payments' (Stripe raw) / 'refunds' → keep collected
    return {
      label: 'Total collected',
      amount: rangeTotals?.total_collected || 0,
      format: (v) => `$${v.toFixed(2)}`,
      color: '#06D6A0',
      testId: 'range-collected',
      title: `Sum of payments received · ${rangeLabel}`,
    };
  }, [activeTab, outstanding, cancellations, rangeTotals, rangeLabel]);

  // iter-142 — Middle summary card swaps based on active tab.
  // First card (Total Collected) + third card (Net Revenue) stay unchanged
  // across all tabs. Ledger and End-of-Day don't render the bar at all.
  // (Must be declared BEFORE any early return so React hooks rules pass.)
  const middleCard = useMemo(() => {
    if (activeTab === 'outstanding') {
      // Sum of `outstanding` across the currently-loaded outstanding list
      // (already scoped to the date range server-side).
      const sum = outstanding.reduce((s, b) => s + Number(b.outstanding || 0), 0);
      return {
        label: 'Total outstanding',
        amount: sum,
        color: '#F59E0B',          // amber — money owed
        testId: 'range-outstanding',
        title: `Sum of outstanding balances · ${rangeLabel}`,
      };
    }
    if (activeTab === 'cancellations') {
      // Sum of booking total across cancellation rows. iter-142 enriches
      // each row with `booking_total` (joined from db.bookings).
      const sum = cancellations.reduce((s, c) => s + Number(c.booking_total || c.total_paid || 0), 0);
      return {
        label: 'Total cancelled amount',
        amount: sum,
        color: '#EF4444',          // red — lost revenue
        testId: 'range-cancelled-total',
        title: `Sum of party totals for cancelled bookings · ${rangeLabel}`,
      };
    }
    // 'today' / 'payments' (Stripe raw) / 'refunds' → keep refunds as the middle metric
    return {
      label: 'Total refunded',
      amount: rangeTotals?.total_refunds || 0,
      color: '#FF3B6B',
      testId: 'range-refunded',
      title: `Sum of refunds · ${rangeLabel}`,
    };
  }, [activeTab, outstanding, cancellations, rangeTotals, rangeLabel]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  // Helpful counts displayed in subtitle
  const countsByMethod = rangeTotals?.totals_by_method || {};
  const ledgerOnly = activeTab === 'ledger';
  const registerOnly = activeTab === 'register';

  return (
    <div>
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Payments & Refunds</h1>
          <p className="text-[#6B6B7A]">{payments.length} transactions | {refundInsights?.refund_count ?? refunds.length} refunds | {cancellations.length} cancellations</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button onClick={() => setPosOpen(true)} className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="pos-new-payment-btn">
            <Wallet className="w-4 h-4 mr-2" /> Take Payment
          </Button>
          <Button onClick={() => setRefundOpen(true)} className="bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="process-refund-btn">
            <ArrowDownRight className="w-4 h-4 mr-2" /> Process Refund
          </Button>
        </div>
      </div>

      {/* Pending e-Transfers — only renders when there are items */}
      <PendingEtransfersWidget onChange={refreshAllRangeTabs} />

      {/* Tab toggle */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={() => setActiveTab('today')} variant={activeTab === 'today' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'today' ? 'bg-[#1E1E24] text-white' : 'border-[#E8E5DF]'}`} data-testid="tab-today">
          <BarChart3 className="w-4 h-4 mr-1" /> Summary
        </Button>
        <Button onClick={() => setActiveTab('register')} variant={activeTab === 'register' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'register' ? 'bg-[#FF3B6B] text-white' : 'border-[#E8E5DF]'}`} data-testid="tab-register">
          <Lock className="w-4 h-4 mr-1" /> End of Day
        </Button>
        <Button onClick={() => setActiveTab('outstanding')} variant={activeTab === 'outstanding' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'outstanding' ? 'bg-[#06D6A0] text-white' : 'border-[#E8E5DF]'}`} data-testid="tab-outstanding">
          <Wallet className="w-4 h-4 mr-1" /> Outstanding
        </Button>
        <Button onClick={() => setActiveTab('ledger')} variant={activeTab === 'ledger' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'ledger' ? 'bg-[#6366F1] text-white' : 'border-[#E8E5DF]'}`} data-testid="tab-ledger">
          <Receipt className="w-4 h-4 mr-1" /> Ledger
        </Button>
        <Button onClick={() => setActiveTab('payments')} variant={activeTab === 'payments' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'payments' ? 'bg-[#FF3B6B] text-white' : 'border-[#E8E5DF]'}`} data-testid="tab-payments">
          <DollarSign className="w-4 h-4 mr-1" /> Stripe (raw)
        </Button>
        <Button onClick={() => setActiveTab('refunds')} variant={activeTab === 'refunds' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'refunds' ? 'bg-red-500 text-white' : 'border-[#E8E5DF]'}`} data-testid="tab-refunds">
          <ArrowDownRight className="w-4 h-4 mr-1" /> Refunds
        </Button>
        <Button onClick={() => setActiveTab('cancellations')} variant={activeTab === 'cancellations' ? 'default' : 'outline'}
          className={`rounded-full ${activeTab === 'cancellations' ? 'bg-orange-500 text-white' : 'border-[#E8E5DF]'}`} data-testid="tab-cancellations">
          <Ban className="w-4 h-4 mr-1" /> Cancellations
        </Button>
      </div>

      {/* iter-140 — Page-level date range filter. Hidden on Ledger (which
          has its own filter) and on End-of-Day (which is always "today"). */}
      {!ledgerOnly && !registerOnly && (
        <div className="mb-6" data-testid="payments-range-bar-wrap">
          <DateRangeBar
            value={range}
            presetId={rangePreset}
            onPresetChange={setRangePreset}
            onChange={({ start, end, label }) => {
              setRange({ start, end });
              setRangeLabel(label);
            }}
            presets={DEFAULT_PAYMENTS_PRESETS}
            loading={rangeLoading}
            testId="payments-range-bar"
          />
        </div>
      )}

      {/* iter-140 — Always-visible summary cards (replaces the Today-only
          cards). React to whatever the DateRangeBar is set to. */}
      {!ledgerOnly && !registerOnly && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="range-summary-cards">
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center" title={firstCard.title}>
            <p className="text-xs text-[#6B6B7A]">{firstCard.label}</p>
            <p className="text-2xl font-bold" style={{ color: firstCard.color }} data-testid={firstCard.testId}>
              {firstCard.format(Number(firstCard.amount || 0))}
            </p>
            <p className="text-[10px] text-[#6B6B7A] mt-0.5">{rangeLabel}</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center" title={middleCard.title}>
            <p className="text-xs text-[#6B6B7A]">{middleCard.label}</p>
            <p className="text-2xl font-bold" style={{ color: middleCard.color }} data-testid={middleCard.testId}>
              ${Number(middleCard.amount || 0).toFixed(2)}
            </p>
            <p className="text-[10px] text-[#6B6B7A] mt-0.5">{rangeLabel}</p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 text-center">
            <p className="text-xs text-[#6B6B7A]">Net revenue</p>
            <p className="text-2xl font-bold text-[#1E1E24]" data-testid="range-net">
              ${(rangeTotals?.net || 0).toFixed(2)}
            </p>
            <p className="text-[10px] text-[#6B6B7A] mt-0.5">{rangeLabel}</p>
          </div>
        </div>
      )}

      {/* Summary panel (was 'Today') */}
      {activeTab === 'today' && (
        <div className="space-y-4" data-testid="today-panel">
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
            <p className="text-sm font-semibold text-[#1E1E24] mb-3">By payment method · {rangeLabel}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="by-method-grid">
              {Object.entries(countsByMethod).map(([method, amt]) => (
                <div key={method}
                     className="flex items-center justify-between p-2 rounded-lg bg-[#FFFDF8] border border-[#E8E5DF]"
                     data-testid={`by-method-${method}`}>
                  <span className="text-[#6B6B7A] capitalize">{method.replace(/_/g, ' ')}</span>
                  <span className="font-semibold text-[#1E1E24]">${amt.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="today-transactions-card">
            <div className="flex items-center justify-between p-4 border-b border-[#E8E5DF]">
              <p className="text-sm font-semibold text-[#1E1E24]">Transactions · {rangeLabel} ({rangeTransactions.length})</p>
              <Button onClick={() => { fetchRangeTotals(); fetchRangeTransactions(); }} variant="outline" size="sm" className="rounded-full" data-testid="today-tx-refresh">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Recorded by</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="today-tx-rows">
                {rangeTransactions.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-6 text-[#6B6B7A]">No payments in this range.</TableCell></TableRow>
                ) : rangeTransactions.map((r) => (
                  <TableRow key={r.payment_id} data-testid={`today-tx-${r.payment_id}`}>
                    <TableCell className="text-xs">{(r.created_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.type === 'refund' ? 'bg-red-100 text-red-600' : r.type === 'adjustment' ? 'bg-orange-100 text-orange-600' : 'bg-[#06D6A0]/15 text-[#058B69]'}`}>{r.type}</span>
                    </TableCell>
                    <TableCell className="text-xs capitalize">{(r.method || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell className={`text-right font-semibold ${r.amount < 0 ? 'text-[#FF3B6B]' : 'text-[#1E1E24]'}`}>${Number(r.amount).toFixed(2)}</TableCell>
                    <TableCell className="text-xs font-mono text-[#6B6B7A]">{(r.booking_id || '').replace('booking_', '') || '—'}</TableCell>
                    <TableCell className="text-xs">{r.customer_email || '—'}</TableCell>
                    <TableCell className="text-xs">{r.employee_name || '—'}</TableCell>
                    <TableCell className="text-xs text-[#6B6B7A] max-w-[200px] truncate" title={r.notes}>{r.notes || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* End of Day / Daily Register panel — unchanged */}
      {activeTab === 'register' && (
        <DailyRegisterPanel
          currentUserRole={user?.role}
          onRegisterChange={refreshAllRangeTabs}
        />
      )}

      {/* Unified ledger — independent filter (per iter-140 spec) */}
      {activeTab === 'ledger' && (
        <div data-testid="ledger-panel">
          <div className="flex flex-wrap gap-2 mb-4 items-center">
            <Input placeholder="Method (e.g. cash)" value={ledgerFilter.method}
              onChange={(e) => setLedgerFilter({ ...ledgerFilter, method: e.target.value })} className="bg-white max-w-[180px]" data-testid="ledger-method" />
            <Input placeholder="Type (payment/refund/adjustment)" value={ledgerFilter.type}
              onChange={(e) => setLedgerFilter({ ...ledgerFilter, type: e.target.value })} className="bg-white max-w-[220px]" data-testid="ledger-type" />
            <Input type="date" value={ledgerFilter.date_from}
              onChange={(e) => setLedgerFilter({ ...ledgerFilter, date_from: e.target.value })} className="bg-white max-w-[160px]" data-testid="ledger-from" />
            <Input type="date" value={ledgerFilter.date_to}
              onChange={(e) => setLedgerFilter({ ...ledgerFilter, date_to: e.target.value })} className="bg-white max-w-[160px]" data-testid="ledger-to" />
            <Button onClick={fetchLedger} className="rounded-full bg-[#1E1E24] text-white">Apply</Button>
            <Button onClick={() => { setLedgerFilter({ method: '', type: '', date_from: '', date_to: '' }); setTimeout(fetchLedger, 50); }} variant="outline" className="rounded-full">Clear</Button>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Booking / Customer</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Recorded by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="ledger-rows">
                {ledger.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-[#6B6B7A] py-6">No payments match.</TableCell></TableRow>
                ) : ledger.map((r) => (
                  <TableRow key={r.payment_id} data-testid={`ledger-row-${r.payment_id}`}>
                    <TableCell className="text-xs">{(r.created_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${r.type === 'refund' ? 'bg-red-100 text-red-600' : r.type === 'adjustment' ? 'bg-orange-100 text-orange-600' : 'bg-[#06D6A0]/15 text-[#058B69]'}`}>{r.type}</span></TableCell>
                    <TableCell className="text-xs capitalize">{(r.method || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell className={`text-right font-semibold ${r.amount < 0 ? 'text-[#FF3B6B]' : 'text-[#1E1E24]'}`}>${Number(r.amount).toFixed(2)}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-mono">{(r.booking_id || '').replace('booking_', '') || '—'}</div>
                      <div className="text-[#6B6B7A]">{r.customer_email || ''}</div>
                    </TableCell>
                    <TableCell className="text-xs text-[#6B6B7A] max-w-[260px] truncate" title={r.notes}>{r.notes || ''}</TableCell>
                    <TableCell className="text-xs">{r.employee_name || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Outstanding balances panel */}
      {activeTab === 'outstanding' && (
        <div data-testid="outstanding-panel">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Input
              value={outstandingSearch}
              onChange={(e) => setOutstandingSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchOutstanding(outstandingSearch)}
              placeholder="Search by customer, email, or booking ID…"
              className="bg-white max-w-md"
              data-testid="outstanding-search"
            />
            <Button variant="outline" onClick={() => fetchOutstanding(outstandingSearch)} className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <span className="text-xs text-[#6B6B7A]">{outstanding.length} bookings with balance · {rangeLabel}</span>
          </div>
          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="outstanding-rows">
                {outstanding.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-[#6B6B7A] py-6">No outstanding balances.</TableCell></TableRow>
                ) : outstanding.map((b) => {
                  const paid = (b.deposit_paid ? b.deposit_amount : 0) + (b.manual_paid_amount || 0);
                  return (
                    <TableRow key={b.booking_id} data-testid={`outstanding-row-${b.booking_id}`}>
                      <TableCell>
                        <div className="font-medium text-[#1E1E24]">{b.customer_name}</div>
                        <div className="text-[11px] text-[#6B6B7A]">{b.customer_email}</div>
                        {b.lifecycle_status && (
                          <div className="mt-1">
                            <BookingStatusBadge status={b.lifecycle_status} note={b.status_note} testid={`outstanding-status-${b.booking_id}`} />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-[#6B6B7A]">{b.booking_id.replace('booking_', '')}</TableCell>
                      <TableCell>{b.date} <span className="text-[#6B6B7A] text-[11px]">{b.time_slot}</span></TableCell>
                      <TableCell className="text-right">${Number(b.total).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-[#06D6A0]">${paid.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-bold text-[#FF3B6B]" data-testid={`outstanding-amount-${b.booking_id}`}>
                        ${Number(b.outstanding).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => { setPayTarget(b); setPayDialogOpen(true); }}
                          className="bg-[#06D6A0] hover:bg-[#05b88a] text-white rounded-full"
                          data-testid={`pay-now-${b.booking_id}`}
                        >
                          <DollarSign className="w-3 h-3 mr-1" /> Pay Now
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Transaction</TableHead>
                <TableHead>Booking</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6B6B7A]">No Stripe transactions in this range</TableCell></TableRow>
              ) : payments.map(p => (
                <TableRow key={p.transaction_id}>
                  <TableCell className="font-mono text-xs">{p.transaction_id}</TableCell>
                  <TableCell className="text-xs">{p.booking_id}</TableCell>
                  <TableCell><span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">{p.payment_method}</span></TableCell>
                  <TableCell className="text-sm capitalize">{p.payment_type}</TableCell>
                  <TableCell className="font-semibold text-[#06D6A0]">${p.amount?.toFixed(2)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {p.payment_status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-[#6B6B7A]">{new Date(p.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {activeTab === 'refunds' && (
        <div className="space-y-4">
          {refundInsights && (
            <div data-testid="refund-insights">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Total refunded', value: `$${(refundInsights.total_refunded || 0).toFixed(2)}`, color: '#FF3B6B', testId: 'ri-total' },
                  { label: 'Refund count', value: `${refundInsights.refund_count || 0}`, color: '#1E1E24', testId: 'ri-count' },
                  { label: 'Avg refund', value: `$${(refundInsights.avg_refund || 0).toFixed(2)}`, color: '#F59E0B', testId: 'ri-avg' },
                  { label: 'Largest refund', value: `$${(refundInsights.largest_refund || 0).toFixed(2)}`, color: '#6366F1', testId: 'ri-largest' },
                ].map((c) => (
                  <div key={c.testId} className="bg-white rounded-2xl border border-[#E8E5DF] p-4" data-testid={c.testId}>
                    <div className="flex items-center gap-1.5 text-xs text-[#6B6B7A] mb-1">
                      <TrendingDown className="w-3.5 h-3.5" style={{ color: c.color }} /> {c.label}
                    </div>
                    <p className="text-2xl font-semibold" style={{ color: c.color }}>{c.value}</p>
                  </div>
                ))}
              </div>
              {Object.keys(refundInsights.by_method || {}).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="ri-by-method">
                  {Object.entries(refundInsights.by_method).map(([m, amt]) => (
                    <span key={m} className="text-xs px-3 py-1 rounded-full bg-[#FFF0F4] text-[#B11E48] border border-[#FFD3DF] capitalize">
                      {m.replace(/_/g, ' ')}: ${Number(amt).toFixed(2)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="refunds-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Refund ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Booking</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Receipt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!refundInsights?.refunds || refundInsights.refunds.length === 0) ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#6B6B7A]">No refunds in this range</TableCell></TableRow>
              ) : refundInsights.refunds.map(r => (
                <TableRow key={r.refund_id} data-testid={`refund-row-${r.refund_id}`}>
                  <TableCell className="font-mono text-xs">{r.refund_id}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{r.customer_name || '—'}</p>
                    <p className="text-xs text-[#6B6B7A]">{r.customer_email}</p>
                  </TableCell>
                  <TableCell className="text-xs">{r.booking_id ? (
                    <a href={`/crm/bookings/${r.booking_id}`} className="text-[#6366F1] hover:underline">{r.booking_id}</a>
                  ) : '—'}</TableCell>
                  <TableCell className="font-semibold text-red-500">-${r.amount?.toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-[#6B6B7A] capitalize">{(r.method || '').replace(/_/g, ' ')}</TableCell>
                  <TableCell className="text-sm text-[#6B6B7A] max-w-[220px] truncate" title={r.reason}>{r.reason}</TableCell>
                  <TableCell className="text-xs text-[#6B6B7A]">{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" className="rounded-full h-7 px-2 text-xs"
                      onClick={() => window.open(`${API_URL}/api/admin/payments/${r.payment_id}/refund-receipt.pdf`, '_blank', 'noopener')}
                      data-testid={`refund-receipt-${r.refund_id}`}>
                      <Receipt className="w-3 h-3 mr-1" /> PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      )}

      {activeTab === 'cancellations' && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="cancellations-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Refund</TableHead>
                <TableHead>Cancelled By</TableHead>
                <TableHead>Hrs Before</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cancellations.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6B6B7A]">No cancellations in this range</TableCell></TableRow>
              ) : cancellations.map(c => (
                <TableRow key={c.log_id}>
                  <TableCell className="text-xs">{new Date(c.cancelled_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{c.customer_name}</p>
                    <p className="text-xs text-[#6B6B7A]">{c.customer_email}</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    {c.child_name}&apos;s party<br/>
                    <span className="text-xs text-[#6B6B7A]">{c.party_date} @ {c.party_time}</span>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs capitalize">
                      {(c.reason || '').replace('_', ' ')}
                    </span>
                    {c.comment && <p className="text-xs text-[#6B6B7A] mt-1 max-w-[200px] truncate" title={c.comment}>{c.comment}</p>}
                  </TableCell>
                  <TableCell>
                    <p className="font-semibold text-[#06D6A0]">${c.refund_amount?.toFixed(2)}</p>
                    <p className="text-xs text-[#6B6B7A]">{c.refund_percentage}% of ${c.total_paid?.toFixed(2)}</p>
                  </TableCell>
                  <TableCell className="text-sm">{c.employee_name}</TableCell>
                  <TableCell className="text-xs">
                    <span className={`font-semibold ${c.hours_to_party > 24 ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>
                      {c.hours_to_party?.toFixed(1)}h
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Refund Dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Process Refund</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#1E1E24] font-semibold">Booking ID *</Label>
              <Input value={refundBookingId} onChange={e => setRefundBookingId(e.target.value)}
                placeholder="booking_xxxx" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="refund-booking-id" />
            </div>
            <div>
              <Label className="text-[#1E1E24] font-semibold">Amount (CAD) *</Label>
              <Input type="number" step="0.01" value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
                placeholder="0.00" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="refund-amount" />
            </div>
            <div>
              <Label className="text-[#1E1E24] font-semibold">Reason</Label>
              <Textarea value={refundReason} onChange={e => setRefundReason(e.target.value)}
                placeholder="Reason for refund..." rows={3} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="refund-reason" />
            </div>
            <Button onClick={processRefund} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-refund-btn">
              Process Refund
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Take payment dialog (manual payment collection) */}
      <TakePaymentDialog
        open={payDialogOpen}
        onOpenChange={setPayDialogOpen}
        booking={payTarget}
        onSuccess={() => fetchOutstanding(outstandingSearch)}
      />

      {/* Standalone walk-in POS — charge any amount to a new/existing customer */}
      <PaymentModal
        open={posOpen}
        onOpenChange={setPosOpen}
        mode="admin"
        posMode
        title="Take Payment"
        onSuccess={() => refreshAllRangeTabs()}
      />
    </div>
  );
};

export default CrmPayments;
